<template>
  <template v-if="sidebarComponents.length === 0">
    <section
      v-if="variants.v1 === variant && sidebarComponents.length === 0"
      class="about-section section-padding"
      :style="`background-color: var(--c-${vars.backgroundColor})`"
    >
      <div class="container">
        <div
          class="tw-mb-8 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between lg:tw-flex-row lg:tw-items-start"
        >
          <h2 :style="`color: var(--c-${vars.titleColor});`" class="tw-text-center tw-text-start lg:tw-text-start">
            {{ vars.titleText }}
          </h2>
        </div>

        <div class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-gap-x-10 xl:tw-flex-col-reverse">
          <MotionGroup
            :preset="vars.animationText ? vars.animationText : 'fadeVisible'"
            class="tw-w-full lg:tw-max-w-full"
            :duration="800"
            is="div"
          >
            <Swiper
              :modules="[SwiperPagination]"
              :pagination="false"
              :slides-per-view="1"
              :space-between="24"
              effect="fade"
              :clickable="true"
              :watch-overflow="true"
              @slideChange="onSlideChange"
              @swiper="onSwiper"
              :breakpoints="{
                320: { slidesPerView: 1, spaceBetween: 20 },
                1024: { slidesPerView: 2, spaceBetween: 32 },
                1280: { slidesPerView: 3, spaceBetween: 48 },
              }"
              class="tw-w-full lg:tw-max-w-full"
            >
              <SwiperSlide v-for="(blog, index) in blogs" :key="`blog-${index}`">
                <lazy-theme9-blogs-blog-card :blog="blog" :index="index" version="v2" />
              </SwiperSlide>
            </Swiper>
          </MotionGroup>

          <!-- Slider Pagination -->
          <div class="tw-mt-8 tw-self-start md:tw-self-end" v-if="swiper">
            <LibrarySliderPagination
              :swiper="swiper"
              variant="v8"
              :can-paginate-back="canPaginateBack"
              :can-paginate-next="canPaginateNext"
            />
          </div>
        </div>
      </div>
    </section>
  </template>

  <template v-else-if="sidebarComponents.length > 0">
    <section v-if="variants.v2 === variant" :style="`background-color: var(--c-${vars.backgroundColor})`">
      <div class="container">
        <div
          class="tw-mb-8 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between lg:tw-flex-row lg:tw-items-start"
        >
          <h2 :style="`color: var(--c-${vars.titleColor});`" class="tw-text-center tw-text-start lg:tw-text-start">
            {{ vars.titleText }}
          </h2>
        </div>

        <div class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-start tw-gap-x-10">
          <MotionGroup
            :preset="vars.animationText ? vars.animationText : 'fadeVisible'"
            class="tw-w-full lg:tw-max-w-full"
            :duration="800"
            is="div"
          >
            <Swiper
              :modules="[SwiperPagination]"
              :pagination="false"
              :slides-per-view="1"
              :space-between="24"
              effect="fade"
              :clickable="true"
              :watch-overflow="true"
              @slideChange="onSlideChange"
              @swiper="onSwiper"
              class="tw-w-full lg:tw-max-w-full"
            >
              <SwiperSlide v-for="(blog, index) in blogs" :key="`blog-${index}`">
                <lazy-theme9-blogs-blog-card :blog="blog" :index="index" version="v2" />
              </SwiperSlide>
            </Swiper>
          </MotionGroup>

          <!-- Slider Pagination -->
          <div v-if="swiper">
            <LibrarySliderPagination
              :swiper="swiper"
              variant="v8"
              :can-paginate-back="canPaginateBack"
              :can-paginate-next="canPaginateNext"
            />
          </div>
        </div>
      </div>
    </section>
    <section v-if="variants.v3 === variant" :style="`background-color: var(--c-${vars.backgroundColor})`">
      <div class="container">
        <div
          class="tw-mb-8 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between lg:tw-flex-row lg:tw-items-start"
        >
          <h2 :style="`color: var(--c-${vars.titleColor});`" class="tw-text-center tw-text-start lg:tw-text-start">
            {{ vars.titleText }}
          </h2>
        </div>

        <div class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-start tw-gap-x-10">
          <MotionGroup
            :preset="vars.animationText ? vars.animationText : 'fadeVisible'"
            class="tw-w-full lg:tw-max-w-full"
            :duration="800"
            is="div"
          >
            <Swiper
              :modules="[SwiperPagination]"
              :pagination="false"
              :slides-per-view="1"
              :space-between="24"
              effect="fade"
              :clickable="true"
              :watch-overflow="true"
              @slideChange="onSlideChange"
              @swiper="onSwiper"
              class="tw-w-full lg:tw-max-w-full"
            >
              <SwiperSlide v-for="(blog, index) in blogs" :key="`blog-${index}`">
                <lazy-theme9-blogs-blog-card :blog="blog" :index="index" version="v3" />
              </SwiperSlide>
            </Swiper>
          </MotionGroup>

          <!-- Slider Pagination -->
          <div v-if="swiper">
            <LibrarySliderPagination
              :swiper="swiper"
              variant="v8"
              :can-paginate-back="canPaginateBack"
              :can-paginate-next="canPaginateNext"
            />
          </div>
        </div>
      </div>
    </section>
  </template>
</template>

<script lang="ts">
import type { Component } from '~/types';
import indexGlobal from '~/mixins/index.global';
import { BlogsMixin } from '~/units/blogs/mixins/blogs.mixin';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useBlogsStore } from '~/units/blogs/store';
import { ROUTES } from '~/constants/ui.constants';
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';

// Swiper importu unutma!
import { SwiperPagination } from 'swiper/modules';

export default defineNuxtComponent({
  name: 'Theme9OurBlog',

  mixins: [BlogsMixin, indexGlobal, ComponentMixin],

  async setup(props: { component: Component }) {
    const blogsStore = useBlogsStore();
    await blogsStore.fetchBlogsInPage(props.component);
  },

  data() {
    return {
      swiper: null,
      canPaginateBack: true,
      canPaginateNext: true,
      variants: {
        v1: 'v1',
        v2: 'v2',
      },
    };
  },

  computed: {
    ROUTES() {
      return ROUTES;
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
    ...mapState(useWebsiteStore, ['sidebarComponents']),
  },

  methods: {
    onSwiper(swiper) {
      this.swiper = swiper;
      this.onSlideChange();
    },

    onSlideChange() {
      if (!this.swiper) return;
      const isAtStart = this.swiper.isBeginning;
      const isAtEnd = this.swiper.isEnd;
      if (isAtStart) this.canPaginateBack = false;
      else this.canPaginateBack = true;
      if (isAtEnd) this.canPaginateNext = false;
      else this.canPaginateNext = true;
    },
  },

  mounted() {
    if (this.swiper) {
      this.onSlideChange();
    }
  },
});
</script>
